// src/pages/UploadPage.jsx
// Choose (or drop) the team's .xlsx workbook. While it is read the page shows
// progress; once loaded it lists what was found — sheets, games, date range —
// and which page reads what, so it is clear what every screen is built from.
// The file is only read in this browser; nothing is uploaded anywhere.

import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  ShieldCheck,
  UploadCloud,
  AlertTriangle,
} from "lucide-react";
import { Badge, Button, Callout, Card, Page, PageHeader } from "../ui/kit";
import { ymd } from "../ui/format";

const ACCEPT = ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Which sheets feed which page (the parsers pick the first that exists).
const SHEET_USES = [
  { test: /^all_scrim$|^all_scrim_stats$/i, use: "ภาพรวมทีม · Team Performance" },
  { test: /scrim/i, use: "Draft Recap · Tier Data · Draft · Tactic · Minimap" },
  { test: /combo/i, use: "คอมโบฮีโร่ (Tier Data / Draft)" },
  { test: /rpl|apl|stats/i, use: "สถิติลีก / ทีมคู่แข่ง" },
  { test: /early|macro/i, use: "Early Macro (Team Performance)" },
];

function sheetUse(name) {
  const hit = SHEET_USES.find((u) => u.test.test(name));
  return hit ? hit.use : "";
}

export default function UploadPage({ onFile, processing, error, workbook }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const pick = (file) => {
    if (!file || processing) return;
    onFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && /\.xlsx$/i.test(file.name)) pick(file);
  };

  const sheets = useMemo(() => workbook?.sheets || [], [workbook]);
  const loaded = Boolean(workbook?.name) && !processing;

  return (
    <Page>
      <PageHeader
        eyebrow="ข้อมูล"
        icon={FileSpreadsheet}
        title="ไฟล์ข้อมูลทีม"
        subtitle="เลือกไฟล์ Excel ของทีม (.xlsx) — ทุกหน้าคำนวณจากไฟล์นี้ ไฟล์ถูกอ่านในเบราว์เซอร์นี้เท่านั้น ไม่ถูกส่งออกไปไหน"
        actions={
          loaded ? (
            <Button variant="primary" iconRight={ArrowRight} onClick={() => navigate("/dashboard")}>
              ไปหน้าภาพรวมทีม
            </Button>
          ) : null
        }
      />

      <div className="ds-grid" style={{ gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)", alignItems: "start" }} data-upload-grid>
        <div
          role="button"
          tabIndex={0}
          aria-busy={processing}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
          style={{
            position: "relative",
            minHeight: 320,
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            padding: "40px 24px",
            borderRadius: "var(--r-xl)",
            border: `1.5px dashed ${over ? "var(--accent)" : "var(--border-3)"}`,
            background: over
              ? "radial-gradient(600px 300px at 50% 0%, rgba(255,107,26,0.14), transparent 70%), var(--surface)"
              : "radial-gradient(600px 300px at 50% 0%, rgba(255,107,26,0.07), transparent 70%), var(--surface)",
            cursor: processing ? "progress" : "pointer",
            transition: "border-color .15s, background .2s",
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            style={{ display: "none" }}
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <div style={{ display: "grid", justifyItems: "center", gap: 14, maxWidth: 460 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 18,
                display: "grid",
                placeItems: "center",
                background: "var(--accent-soft)",
                border: "1px solid var(--accent-line)",
                color: "var(--accent)",
              }}
            >
              {processing ? <Loader2 size={28} className="ds-spin" /> : <UploadCloud size={28} />}
            </div>
            <div>
              <div style={{ fontSize: 19, fontWeight: 600, color: "var(--text)" }}>
                {processing ? "กำลังอ่านไฟล์…" : loaded ? "ลากไฟล์ใหม่มาวาง เพื่อเปลี่ยนไฟล์" : "ลากไฟล์ .xlsx มาวางที่นี่"}
              </div>
              <div style={{ marginTop: 6, color: "var(--text-3)", fontSize: 13 }}>
                {processing ? "แยกชีท สร้างสถิติทีม คอมโบ และดราฟต์ — ไฟล์ใหญ่อาจใช้เวลาหลายวินาที" : "หรือคลิกเพื่อเลือกไฟล์จากเครื่อง (FS ROV … .xlsx)"}
              </div>
            </div>
            {processing ? (
              <div style={{ width: 260, height: 4, borderRadius: 4, background: "var(--surface-3)", overflow: "hidden" }}>
                <div className="ds-indeterminate" />
              </div>
            ) : (
              <span className="ds-btn ds-btn--primary ds-btn--lg" aria-hidden>
                <UploadCloud size={16} /> เลือกไฟล์
              </span>
            )}
          </div>
        </div>

        <div className="ds-grid" style={{ gap: 12 }}>
          {error ? (
            <Callout tone="warn" icon={AlertTriangle}>
              อ่านไฟล์ไม่สำเร็จ: {error}
            </Callout>
          ) : null}

          {loaded ? (
            <Card
              title={String(workbook.name).replace(/\.(xlsx|xls)$/i, "")}
              icon={CheckCircle2}
              subtitle="โหลดแล้ว — ใช้กับทุกหน้าจนกว่าจะปิดหรือรีเฟรชเบราว์เซอร์"
              tone="accent"
            >
              <dl className="ds-kv">
                <dt>เกมในบันทึกสครีม (ภาพรวม)</dt>
                <dd>{(workbook.games || 0).toLocaleString("en-US")}</dd>
                <dt>เกมที่มีดราฟต์ (หน้าดราฟต์/ทักติก)</dt>
                <dd>{(workbook.draftGames || 0).toLocaleString("en-US")}</dd>
                <dt>คอมโบฮีโร่</dt>
                <dd>{(workbook.combos || 0).toLocaleString("en-US")}</dd>
                <dt>ช่วงวันที่</dt>
                <dd>
                  {workbook.first ? `${ymd(workbook.first)} – ${ymd(workbook.latest)}` : "–"}
                </dd>
                <dt>คู่แข่ง</dt>
                <dd>{workbook.opponents || 0} ทีม</dd>
                <dt>จำนวนชีท</dt>
                <dd>{sheets.length}</dd>
              </dl>
            </Card>
          ) : (
            <Card title="ไฟล์ที่ใช้ได้" icon={FileSpreadsheet} subtitle="ไฟล์บันทึกของทีมรูปแบบเดิม ไม่ต้องแก้ไขอะไรในชีท">
              <ul style={{ margin: 0, paddingLeft: 18, color: "var(--text-2)", fontSize: 13, lineHeight: 1.8 }}>
                <li>
                  ชีท <b>All_Scrim</b> — ผลทุกเกม (ภาพรวมทีม, Team Performance)
                </li>
                <li>
                  ชีท <b>…_Scrim</b> (เช่น APL_Scrim) — แบน/พิกรายเกม (Draft Recap, Tier Data, Tactic, Minimap)
                </li>
                <li>
                  ชีท <b>Combo_Stats</b> — คอมโบฮีโร่ (ถ้ามี)
                </li>
              </ul>
            </Card>
          )}

          <Callout tone="info" icon={ShieldCheck}>
            ไฟล์ Excel เป็นต้นฉบับของทีม — ระบบอ่านอย่างเดียว ไม่แก้ไขหรือบันทึกทับไฟล์
          </Callout>
        </div>
      </div>

      {loaded && sheets.length ? (
        <Card title="ชีทในไฟล์" subtitle="ชีทที่ระบบรู้จักจะบอกว่าหน้าไหนใช้" style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 8 }}>
            {sheets.map((name) => {
              const use = sheetUse(name);
              return (
                <div
                  key={name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 11px",
                    borderRadius: 10,
                    background: "var(--bg-elev)",
                    border: "1px solid var(--border)",
                    minWidth: 0,
                  }}
                >
                  <FileSpreadsheet size={15} color={use ? "var(--win)" : "var(--text-4)"} style={{ flex: "none" }} />
                  <div style={{ minWidth: 0 }}>
                    <div className="ds-truncate" style={{ fontSize: 13, fontWeight: 600, color: use ? "var(--text)" : "var(--text-2)" }}>
                      {name}
                    </div>
                    {use ? <div className="ds-truncate" style={{ fontSize: 11.5, color: "var(--text-3)" }}>{use}</div> : null}
                  </div>
                  {use ? (
                    <Badge tone="win" style={{ marginLeft: "auto" }}>
                      ใช้
                    </Badge>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      <style>{`
        @media (max-width: 900px) { [data-upload-grid] { grid-template-columns: 1fr !important; } }
        .ds-spin { animation: ds-spin 0.9s linear infinite; }
        @keyframes ds-spin { to { transform: rotate(360deg); } }
        .ds-indeterminate { width: 40%; height: 100%; border-radius: 4px; background: var(--accent); animation: ds-indet 1.1s ease-in-out infinite; }
        @keyframes ds-indet { 0% { transform: translateX(-110%); } 100% { transform: translateX(260%); } }
      `}</style>
    </Page>
  );
}
